import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { ConfigService } from 'src/app/services/config.service';
import { FileWatchService } from 'src/app/services/file-watch.service';
import { WidgetContextService } from 'src/app/services/widget-context.service';
import { DialogService } from '../../../services/dialog.service';

export type MarkdownTab = 'preview' | 'source';

@Component({
    selector: 'app-markdown-widget',
    templateUrl: './markdown-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './markdown-widget.component.scss'],
    standalone: false
})
export class MarkdownWidgetComponent extends BaseWidgetComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private dialogs = inject(DialogService);
  @ViewChild('markdownContainer') markdownContainer: ElementRef<HTMLDivElement>;

  /** URL to load markdown from (mutually exclusive with content) */
  @Input() src: string = '';

  /** Direct markdown content (mutually exclusive with src) */
  @Input() content: string = '';

  /** Widget title shown in titlebar */
  @Input() title: string = 'Markdown';

  /** File path for edit functionality (extracted from src URL) */
  @Input() filePath: string = '';

  /** Start in edit mode instead of preview */
  @Input() startInEditMode: boolean = false;

  /** Emitted when user clicks Edit button (legacy - opens Monaco widget) */
  @Output() onEditFile = new EventEmitter<{ parentId: string; path: string }>();

  // Tab state
  public activeTab: MarkdownTab = 'preview';

  // Preview state
  public loading = false;
  public error: string | null = null;

  // Source editor state
  public editorContent: string = '';
  public originalContent: string = '';
  public hasUnsavedChanges = false;
  public saving = false;
  public shareCopied = false;
  public pathCopied = false;

  // Scroll position tracking
  private previewScrollTop = 0;
  private editorScrollTop = 0;

  public editorOptions: any = {
    theme: 'vs-dark',
    language: 'markdown',
    automaticLayout: true,
    wordWrap: 'on',
    scrollBeyondLastLine: false,
    minimap: { enabled: false },
  };

  private editorInstance: any = null;
  private fileWatchSub: Subscription | null = null;
  private imageFetchSub = new Subscription();
  private imageObjectUrls: string[] = [];
  private watchedPath: string | null = null;
  private restoreScrollAfterLoad = false;

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private fileWatch: FileWatchService,
    private _widgetContext: WidgetContextService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.startInEditMode) {
      this.activeTab = 'source';
    }
    this.loadContent();
    window.addEventListener('keydown', this.handleKeydown.bind(this));
    this._widgetContext.updateContext({
      widgetId: this.id,
      widgetType: 'MarkdownWidget',
      path: this.filePath || this.src,
      state: { title: this.title },
    });
  }

  ngAfterViewInit(): void {
    // Restore scroll position after view initializes
    this.restorePreviewScroll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['src'] || changes['content']) {
      this.loadContent();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this.handleKeydown.bind(this));
    this.unwatchFile();
    this.clearResolvedImages();
    this._widgetContext.removeContext(this.id);
    super.ngOnDestroy();
  }

  /** Handle keyboard shortcuts: Ctrl+S to save, Ctrl+Shift+O to open */
  private handleKeydown(event: KeyboardEvent): void {
    // Only handle if this widget is focused (check if event target is within our component)
    const target = event.target as HTMLElement;
    const isInEditor = target.closest('.monaco-editor') || target.closest('app-markdown-widget');
    if (!isInEditor) return;

    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      this.saveWithPrompt();
    } else if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'O') {
      event.preventDefault();
      this.openWithPrompt();
    }
  }

  /** Get subtitle hint for titlebar */
  getSubtitle(): string | null {
    if (this.hasUnsavedChanges) {
      return 'Ctrl+S to save';
    }
    if (!this.filePath && !this.hasPathInSrc()) {
      return 'Ctrl+Shift+O to open';
    }
    return null;
  }

  /** Open a file - prompt for path */
  async openWithPrompt(): Promise<void> {
    const path = await this.dialogs.prompt('Open markdown file path:');
    if (path && path.trim()) {
      // Warn about unsaved changes
      if (this.hasUnsavedChanges) {
        const confirmed = await this.dialogs.confirm('You have unsaved changes. Discard them?');
        if (!confirmed) return;
      }
      this.loadFromPath(path.trim());
    }
  }

  /** Load content from a file path or URL */
  private loadFromPath(path: string): void {
    const isUploadUrl = path.startsWith('/mux/') || path.startsWith('http');
    this.filePath = isUploadUrl ? '' : path;
    this.src = isUploadUrl ? path : `${ConfigService.muxUrl}/commander/file?path=${encodeURIComponent(path)}`;
    this.title = path.split('/').pop() || 'Markdown';
    this.loadContent();
  }

  /** Save - prompt for path if this is a new document */
  async saveWithPrompt(): Promise<void> {
    if (!this.filePath && !this.hasPathInSrc()) {
      // New document - prompt for save location
      const path = await this.dialogs.prompt('Save as (enter file path):');
      if (path && path.trim()) {
        this.filePath = path.trim();
        this.title = this.filePath.split('/').pop() || 'Markdown';
        this.save();
      }
    } else if (this.hasUnsavedChanges) {
      this.save();
    }
  }

  /** Switch to a tab */
  setTab(tab: MarkdownTab): void {
    if (tab === this.activeTab) return;

    // Save scroll position before switching
    if (this.activeTab === 'preview') {
      this.savePreviewScroll();
    } else if (this.activeTab === 'source') {
      this.saveEditorScroll();
    }

    this.activeTab = tab;

    // Restore scroll position after Angular renders the new tab
    setTimeout(() => {
      if (tab === 'preview') {
        this.restorePreviewScroll();
      } else if (tab === 'source') {
        this.restoreEditorScroll();
      }
    }, 0);
  }

  private savePreviewScroll(): void {
    if (this.markdownContainer?.nativeElement) {
      this.previewScrollTop = this.markdownContainer.nativeElement.scrollTop;
    }
  }

  private restorePreviewScroll(): void {
    if (this.markdownContainer?.nativeElement) {
      this.markdownContainer.nativeElement.scrollTop = this.previewScrollTop;
    }
  }

  private saveEditorScroll(): void {
    if (this.editorInstance) {
      this.editorScrollTop = this.editorInstance.getScrollTop();
    }
  }

  private restoreEditorScroll(): void {
    if (this.editorInstance) {
      this.editorInstance.setScrollTop(this.editorScrollTop);
    }
  }

  /** Called when Monaco editor is initialized */
  onEditorInit(editor: any): void {
    this.editorInstance = editor;
    // Restore scroll position if we have one saved
    if (this.editorScrollTop > 0) {
      setTimeout(() => this.restoreEditorScroll(), 0);
    }
  }

  /** Called when editor content changes */
  onEditorChange(): void {
    this.hasUnsavedChanges = this.editorContent !== this.originalContent;
  }

  /** Get the content to display in preview (current edits, not saved) */
  get previewContent(): string {
    return this.editorContent || this.originalContent;
  }

  /** Called when ngx-markdown finishes rendering preview content */
  onMarkdownReady(): void {
    setTimeout(() => this.resolveProtectedImages(), 0);
  }

  /** Discard changes and revert to original */
  discard(): void {
    this.editorContent = this.originalContent;
    this.hasUnsavedChanges = false;
  }

  /** Save the edited content */
  save(): void {
    const path = this.getEditPath();
    if (!path || this.saving) return;

    this.saving = true;

    this.http.post<any>(`${ConfigService.muxUrl}/commander/write_file`, {
      path: path,
      content: this.editorContent,
      ...(this.hostFromSrc() ? { host: this.hostFromSrc() } : {})
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.originalContent = this.editorContent;
          this.hasUnsavedChanges = false;
          console.log(`Saved: ${path}`);
        } else {
          console.error('Save failed:', response.error);
        }
        this.saving = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Save failed:', err);
        this.saving = false;
        this.cdr.detectChanges();
      }
    });
  }

  /** Override close to warn about unsaved changes */
  override async close(): Promise<void> {
    if (this.hasUnsavedChanges) {
      const confirmed = await this.dialogs.confirm('You have unsaved changes. Discard them?');
      if (!confirmed) return;
    }
    super.close();
  }

  private loadContent(): void {
    this.error = null;
    this.clearResolvedImages();

    if (this.content) {
      // Direct content provided
      this.editorContent = this.content;
      this.originalContent = this.content;
      this.loading = false;
      this.cdr.detectChanges();
    } else if (this.src) {
      // Load from URL
      this.loading = true;
      this.cdr.detectChanges();
      this.http.get(this.src, { responseType: 'text', observe: 'response' }).subscribe({
        next: (resp) => {
          const data = resp.body ?? '';
          this.uploadFilePath = resp.headers.get('X-Lit-File-Path');
          this.editorContent = data;
          this.originalContent = data;
          this.loading = false;
          this.cdr.detectChanges();
          // Restore scroll position after file-watch reload
          if (this.restoreScrollAfterLoad) {
            this.restoreScrollAfterLoad = false;
            setTimeout(() => {
              this.restorePreviewScroll();
              this.restoreEditorScroll();
            }, 0);
          }
          // Start watching the file for external changes
          const path = this.getEditPath();
          console.log('[MarkdownWidget] loadContent complete, path for file watch:', path, 'src:', this.src);
          if (path) {
            this.watchFilePath(path);
          }
        },
        error: (err) => {
          this.error = `Failed to load: ${err.message || err.statusText || 'Unknown error'}`;
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
    } else {
      this.editorContent = '';
      this.originalContent = '';
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  /** Check if we can edit (have a file path) */
  get canEdit(): boolean {
    return !!this.filePath || this.hasPathInSrc();
  }

  /** Check if we can share (have a file path to link to) */
  get canShare(): boolean {
    return !!this.getEditPath();
  }

  /** On-disk path reported by the upload route (X-Lit-File-Path) when the file
   *  was opened by URL — an attachment, say — so "Copy file path" still works
   *  even though the widget has no commander path to edit through. */
  uploadFilePath: string | null = null;

  get canCopyPath(): boolean {
    return !!(this.getEditPath() || this.uploadFilePath);
  }

  /** Copy the file path to the clipboard */
  copyFilePath(): void {
    const path = this.getEditPath() || this.uploadFilePath;
    if (!path) return;
    navigator.clipboard.writeText(path).then(() => {
      this.pathCopied = true;
      this.cdr.detectChanges();
      setTimeout(() => { this.pathCopied = false; this.cdr.detectChanges(); }, 2000);
    });
  }

  /** Copy a shareable deep-link URL for this file to the clipboard */
  copyShareLink(): void {
    const path = this.getEditPath();
    if (!path) return;
    const baseHash = window.location.hash.split('?')[0];
    const shareUrl = `${window.location.origin}${window.location.pathname}${baseHash}?widget=MarkdownWidget&path=${encodeURIComponent(path)}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      this.shareCopied = true;
      this.cdr.detectChanges();
      setTimeout(() => { this.shareCopied = false; this.cdr.detectChanges(); }, 2000);
    });
  }

  /** Extract path from src URL if it's a commander/file URL */
  private hasPathInSrc(): boolean {
    return this.src?.includes('/commander/file?path=');
  }

  /** Get the file path for editing */
  /** The linked computer named in the src URL (`&host=`), '' for the home. */
  public hostFromSrc(): string {
    const m = (this.src || '').match(/[?&]host=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  public getEditPath(): string {
    if (this.filePath) {
      return this.filePath;
    }
    // Extract from src URL: /commander/file?path=<encoded_path>
    if (this.hasPathInSrc()) {
      const match = this.src.match(/path=([^&]+)/);
      if (match) {
        return decodeURIComponent(match[1]);
      }
    }
    return '';
  }

  /** Legacy edit method - opens external Monaco widget */
  edit(): void {
    const path = this.getEditPath();
    if (path) {
      this.onEditFile.emit({ parentId: this.id, path });
    }
  }

  refresh(): void {
    this.loadContent();
  }

  /** Start watching a file path for external changes */
  private watchFilePath(path: string): void {
    console.log('[MarkdownWidget] watchFilePath called:', path, 'current watched:', this.watchedPath);
    if (this.watchedPath === path) return;
    this.unwatchFile();
    this.watchedPath = path;
    this.fileWatch.watchFile(path);
    console.log('[MarkdownWidget] fileWatch.watchFile() called, subscribing to changes');
    this.fileWatchSub = this.fileWatch.getChanges(path).subscribe((event) => {
      console.log('[MarkdownWidget] file change event received:', event);
      // Don't auto-reload if user has unsaved edits
      if (!this.hasUnsavedChanges) {
        // Preserve scroll position across reload
        this.savePreviewScroll();
        this.saveEditorScroll();
        this.restoreScrollAfterLoad = true;
        this.loadContent();
      }
    });
  }

  /** Stop watching the current file */
  private unwatchFile(): void {
    if (this.fileWatchSub) {
      this.fileWatchSub.unsubscribe();
      this.fileWatchSub = null;
    }
    if (this.watchedPath) {
      this.fileWatch.unwatchFile(this.watchedPath);
      this.watchedPath = null;
    }
  }

  private clearResolvedImages(): void {
    this.imageFetchSub.unsubscribe();
    this.imageFetchSub = new Subscription();

    for (const objectUrl of this.imageObjectUrls) {
      URL.revokeObjectURL(objectUrl);
    }
    this.imageObjectUrls = [];
  }

  private resolveProtectedImages(): void {
    const container = this.markdownContainer?.nativeElement;
    if (!container || this.activeTab !== 'preview') {
      return;
    }

    const images = Array.from(container.querySelectorAll<HTMLImageElement>('.markdown-content img'));
    for (const image of images) {
      const rawSrc = image.getAttribute('src')?.trim() || '';
      const fetchUrl = this.getAuthenticatedImageUrl(rawSrc);

      if (!fetchUrl) {
        continue;
      }

      if (image.dataset['resolvedSrc'] === fetchUrl || image.dataset['resolveState'] === 'loading') {
        continue;
      }

      image.dataset['resolveState'] = 'loading';
      image.dataset['resolvedSrc'] = fetchUrl;

      const sub = this.http.get(fetchUrl, { responseType: 'blob' }).subscribe({
        next: (blob) => {
          const objectUrl = URL.createObjectURL(blob);
          this.imageObjectUrls.push(objectUrl);
          image.src = objectUrl;
          image.dataset['resolveState'] = 'loaded';
        },
        error: (err) => {
          image.dataset['resolveState'] = 'error';
          console.error('[MarkdownWidget] Failed to resolve markdown image:', rawSrc, err);
        }
      });

      this.imageFetchSub.add(sub);
    }
  }

  private getAuthenticatedImageUrl(rawSrc: string): string | null {
    if (!rawSrc || rawSrc.startsWith('data:') || rawSrc.startsWith('blob:')) {
      return null;
    }

    if (/^https?:\/\//i.test(rawSrc)) {
      return rawSrc.includes('/commander/file?path=') ? rawSrc : null;
    }

    if (rawSrc.startsWith('/commander/file?path=')) {
      return `${ConfigService.muxUrl}${rawSrc}`;
    }

    if (rawSrc.startsWith(`${ConfigService.muxUrl}/commander/file?path=`) || rawSrc.startsWith('/mux/commander/file?path=')) {
      return rawSrc;
    }

    if (rawSrc.startsWith('/data/') || rawSrc.startsWith('/home/') || rawSrc.startsWith('/opt/')) {
      return `${ConfigService.muxUrl}/commander/file?path=${encodeURIComponent(rawSrc)}`;
    }

    if (rawSrc.startsWith('/')) {
      return null;
    }

    const markdownPath = this.getEditPath();
    if (!markdownPath) {
      return null;
    }

    const lastSlash = markdownPath.lastIndexOf('/');
    const baseDir = lastSlash >= 0 ? markdownPath.substring(0, lastSlash) : '';
    const resolvedPath = this.resolveRelativePath(baseDir, rawSrc);
    return `${ConfigService.muxUrl}/commander/file?path=${encodeURIComponent(resolvedPath)}`;
  }

  private resolveRelativePath(baseDir: string, relativePath: string): string {
    const stack = baseDir.split('/').filter(Boolean);
    const cleanPath = relativePath.split(/[?#]/)[0];

    for (const segment of cleanPath.split('/')) {
      if (!segment || segment === '.') {
        continue;
      }
      if (segment === '..') {
        stack.pop();
        continue;
      }
      stack.push(segment);
    }

    return `/${stack.join('/')}`;
  }
}
